/* =========================================================================
   Bloomelle v2 - global JS
   Drawer open/close, popup fire logic, sticky CTA scroll, /cart/add.js helper.
   ========================================================================= */

(function () {
  'use strict';

  // Public namespace
  window.bl = window.bl || {};

  // ---------- Cart drawer ---------------------------------------------------
  const Drawer = {
    el: null,
    backdrop: null,
    lastFocus: null,
    open() {
      if (!this.el) this.el = document.querySelector('[data-bl-drawer]');
      if (!this.backdrop) this.backdrop = document.querySelector('[data-bl-drawer-backdrop]');
      if (!this.el) return;
      this.lastFocus = document.activeElement;
      this.el.classList.add('is-open');
      if (this.backdrop) this.backdrop.classList.add('is-open');
      document.body.classList.add('bl-no-scroll');
      this.el.setAttribute('aria-hidden', 'false');
      this.el.setAttribute('aria-modal', 'true');
      const focusable = this.el.querySelector('[data-bl-drawer-close], a, button');
      if (focusable) setTimeout(() => focusable.focus(), 50);
    },
    close() {
      if (!this.el) this.el = document.querySelector('[data-bl-drawer]');
      if (!this.backdrop) this.backdrop = document.querySelector('[data-bl-drawer-backdrop]');
      if (!this.el) return;
      this.el.classList.remove('is-open');
      if (this.backdrop) this.backdrop.classList.remove('is-open');
      document.body.classList.remove('bl-no-scroll');
      this.el.setAttribute('aria-hidden', 'true');
      if (this.lastFocus && this.lastFocus.focus) { this.lastFocus.focus(); this.lastFocus = null; }
    },
    toggle() {
      if (!this.el) this.el = document.querySelector('[data-bl-drawer]');
      if (this.el && this.el.classList.contains('is-open')) this.close(); else this.open();
    }
  };

  // ---------- Cart helpers --------------------------------------------------
  async function fetchCart() {
    try {
      const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      return await r.json();
    } catch (e) { return null; }
  }

  function fmtMoney(cents) {
    const v = (cents / 100).toFixed(2);
    return '£' + v;
  }

  function renderDrawer(cart) {
    window.__blCart = cart;
    const el = document.querySelector('[data-bl-drawer-body]');
    if (!el) return;
    if (!cart || cart.item_count === 0) {
      el.innerHTML = `
        <div class="bl-drawer__empty">
          <p class="bl-body-md">your bag is empty.</p>
          <a href="/products/menopause-support-formula" class="bl-btn bl-btn--primary">see the formula</a>
        </div>`;
      return;
    }
    const items = cart.items.map((item, idx) => `
      <div class="bl-drawer__item" data-line-index="${idx + 1}">
        <img src="${item.image || ''}" alt="" />
        <div>
          <div class="bl-drawer__item-name">${item.product_title}</div>
          <div class="bl-drawer__item-plan">${item.variant_title || ''} ${item.selling_plan_allocation ? '· ' + item.selling_plan_allocation.selling_plan.name : ''}</div>
          <div class="bl-drawer__qty">
            <button type="button" data-bl-qty-dec aria-label="decrease">-</button>
            <span>${item.quantity}</span>
            <button type="button" data-bl-qty-inc aria-label="increase">+</button>
          </div>
        </div>
        <div>
          <div class="bl-drawer__item-price">${fmtMoney(item.final_line_price)}</div>
          <a href="#" class="bl-drawer__remove" data-bl-remove>remove</a>
        </div>
      </div>`).join('');

    const discount = (cart.total_discount || 0) > 0
      ? `<div class="bl-drawer__discount"><span>launch pricing applied</span><span>-${fmtMoney(cart.total_discount)}</span></div>`
      : '';

    el.innerHTML = `
      ${items}
      ${discount}
      <div class="bl-drawer__total"><span>total</span><span>${fmtMoney(cart.total_price)}</span></div>
      <div class="bl-drawer__shipping-note">free UK shipping · 90-day money-back · cancel anytime</div>
      <div class="bl-drawer__checkout">
        <a href="/checkout" class="bl-btn bl-btn--primary">check out</a>
      </div>`;
    bindLineActions();
  }

  function bindLineActions() {
    document.querySelectorAll('[data-bl-drawer-body] [data-line-index]').forEach((row) => {
      const idx = parseInt(row.getAttribute('data-line-index'), 10);
      row.querySelector('[data-bl-qty-dec]')?.addEventListener('click', async () => {
        const span = row.querySelector('span');
        const q = Math.max(0, parseInt(span.textContent.trim(), 10) - 1);
        await changeLine(idx, q);
      });
      row.querySelector('[data-bl-qty-inc]')?.addEventListener('click', async () => {
        const span = row.querySelector('span');
        const q = parseInt(span.textContent.trim(), 10) + 1;
        await changeLine(idx, q);
      });
      row.querySelector('[data-bl-remove]')?.addEventListener('click', async (e) => {
        e.preventDefault();
        await changeLine(idx, 0);
      });
    });
  }

  async function changeLine(line, qty) {
    try {
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: line, quantity: qty })
      });
      const cart = await r.json();
      renderDrawer(cart);
      updateCartCounters(cart);
    } catch (e) { console.error('[bl] change line failed', e); }
  }

  function updateCartCounters(cart) {
    const n = cart && cart.item_count ? cart.item_count : 0;
    document.querySelectorAll('[data-bl-cart-count]').forEach((el) => { el.textContent = n; });
  }

  // ---------- Add to cart ---------------------------------------------------
  window.bl.addToCart = async function (variantId, sellingPlanId, price) {
    if (!variantId) return;
    const payload = { id: String(variantId), quantity: 1 };
    if (sellingPlanId) payload.selling_plan = String(sellingPlanId);
    try {
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!r.ok) {
        const err = await r.json();
        console.error('[bl] add to cart failed', err);
        alert(err.description || 'sorry, that did not work. please try again.');
        return;
      }
      // Meta pixel - AddToCart (with value for purchase-value optimisation)
      if (typeof fbq === 'function') {
        var atcPayload = { content_ids: [String(variantId)], content_type: 'product', currency: 'GBP' };
        if (price) atcPayload.value = Number(price) / 100;
        fbq('track', 'AddToCart', atcPayload);
      }
      const cart = await fetchCart();
      renderDrawer(cart);
      updateCartCounters(cart);
      Drawer.open();
    } catch (e) {
      console.error('[bl] add to cart error', e);
    }
  };

  // ---------- Sticky mobile CTA --------------------------------------------
  function initStickyCta() {
    const el = document.querySelector('[data-bl-sticky-cta]');
    if (!el) return;
    document.body.classList.add('bl-has-sticky-cta');
    let visible = false;
    function onScroll() {
      const should = window.scrollY > 200;
      if (should !== visible) {
        visible = should;
        el.classList.toggle('is-visible', should);
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // ---------- Popups --------------------------------------------------------
  function initWelcomePopup() {
    const el = document.querySelector('[data-bl-popup-welcome]');
    if (!el) return;
    const seen = (function(){ try { return localStorage.getItem('bl_welcome_seen'); } catch(e){ return null; } })();
    if (seen) return;
    let fired = false;
    function fire() {
      if (fired) return;
      fired = true;
      el.classList.add('is-open');
      try { localStorage.setItem('bl_welcome_seen', '1'); } catch(e) {}
    }
    setTimeout(fire, 8000);
    window.addEventListener('scroll', function onScroll() {
      const pct = (window.scrollY + window.innerHeight) / document.body.scrollHeight;
      if (pct > 0.25) { fire(); window.removeEventListener('scroll', onScroll); }
    }, { passive: true });
    el.querySelectorAll('[data-bl-popup-close]').forEach((b) => b.addEventListener('click', () => el.classList.remove('is-open')));
    el.addEventListener('click', (e) => { if (e.target === el) el.classList.remove('is-open'); });
  }

  function initExitPopup() {
    const el = document.querySelector('[data-bl-popup-exit]');
    if (!el) return;
    const seen = (function(){ try { return sessionStorage.getItem('bl_exit_seen'); } catch(e){ return null; } })();
    if (seen) return;
    let lastY = 0;
    function showOnce() {
      if (el.classList.contains('is-open')) return;
      el.classList.add('is-open');
      try { sessionStorage.setItem('bl_exit_seen', '1'); } catch(e) {}
    }
    document.addEventListener('mouseout', function (e) {
      if (e.clientY <= 0 && !e.relatedTarget) showOnce();
    });
    window.addEventListener('scroll', function () {
      const y = window.scrollY;
      if (lastY - y > 60 && y < 400) showOnce();
      lastY = y;
    }, { passive: true });
    el.querySelectorAll('[data-bl-popup-close]').forEach((b) => b.addEventListener('click', () => el.classList.remove('is-open')));
    el.addEventListener('click', (e) => { if (e.target === el) el.classList.remove('is-open'); });
  }

  // ---------- Init ----------------------------------------------------------
  document.addEventListener('DOMContentLoaded', async function () {

    // Plan tiles + any add-to-cart button
    document.body.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-bl-add-to-cart]');
      if (btn) {
        e.preventDefault();
        const v = btn.getAttribute('data-variant-id');
        const sp = btn.getAttribute('data-selling-plan') || null;
        const price = btn.getAttribute('data-price') || null;
        window.bl.addToCart(v, sp, price);
      }

      // Reviews carousel arrows
      const rArrow = e.target.closest('[data-bl-reviews-prev], [data-bl-reviews-next]');
      if (rArrow) {
        const wrap = rArrow.closest('[data-bl-reviews]');
        const track = wrap && wrap.querySelector('[data-bl-reviews-track]');
        if (track) {
          const card = track.querySelector('.bl-review');
          const dx = card ? card.offsetWidth + 16 : 360;
          track.scrollBy({ left: rArrow.hasAttribute('data-bl-reviews-prev') ? -dx : dx, behavior: 'smooth' });
        }
      }

      // Meta pixel - InitiateCheckout on any checkout link
      const checkoutLink = e.target.closest('a[href="/checkout"], a[href^="/checkout"]');
      if (checkoutLink && typeof fbq === 'function') {
        const c = window.__blCart;
        fbq('track', 'InitiateCheckout', {
          currency: 'GBP',
          value: c && c.total_price ? c.total_price / 100 : undefined,
          num_items: c && c.item_count ? c.item_count : undefined
        });
      }
      const openBtn = e.target.closest('[data-bl-drawer-open]');
      if (openBtn) { e.preventDefault(); Drawer.open(); }
      const closeBtn = e.target.closest('[data-bl-drawer-close]');
      if (closeBtn) { e.preventDefault(); Drawer.close(); }

      // Mobile menu toggle
      const menuBtn = e.target.closest('[data-bl-menu-toggle]');
      if (menuBtn) {
        e.preventDefault();
        const menu = document.querySelector('[data-bl-mobile-menu]');
        if (menu) {
          const open = menu.classList.toggle('is-open');
          menuBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
          menu.setAttribute('aria-hidden', open ? 'false' : 'true');
          menuBtn.setAttribute('aria-label', open ? 'close menu' : 'open menu');
          document.body.classList.toggle('bl-no-scroll', open);
          if (open) { const f = menu.querySelector('a'); if (f) f.focus(); }
        }
      }
      // Close mobile menu when a link inside it is tapped
      const menuLink = e.target.closest('[data-bl-mobile-menu] a');
      if (menuLink) {
        const menu = document.querySelector('[data-bl-mobile-menu]');
        const tBtn = document.querySelector('[data-bl-menu-toggle]');
        if (menu) { menu.classList.remove('is-open'); menu.setAttribute('aria-hidden', 'true'); }
        if (tBtn) { tBtn.setAttribute('aria-expanded', 'false'); tBtn.setAttribute('aria-label', 'open menu'); }
        document.body.classList.remove('bl-no-scroll');
      }
    });

    // ESC closes drawer, popups and mobile menu
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        Drawer.close();
        document.querySelectorAll('.bl-popup-backdrop.is-open').forEach((p) => p.classList.remove('is-open'));
        const menu = document.querySelector('[data-bl-mobile-menu].is-open');
        if (menu) {
          menu.classList.remove('is-open'); menu.setAttribute('aria-hidden', 'true');
          document.body.classList.remove('bl-no-scroll');
          const tBtn = document.querySelector('[data-bl-menu-toggle]');
          if (tBtn) { tBtn.setAttribute('aria-expanded', 'false'); tBtn.focus(); }
        }
      }
    });

    // Backdrop closes drawer
    const backdrop = document.querySelector('[data-bl-drawer-backdrop]');
    if (backdrop) backdrop.addEventListener('click', () => Drawer.close());

    // Initial cart hydrate (in case there are already items)
    const cart = await fetchCart();
    if (cart) { renderDrawer(cart); updateCartCounters(cart); }

    initStickyCta();
    initWelcomePopup();
    initExitPopup();

    // Video triggers - simple play swap
    document.querySelectorAll('[data-bl-video-play]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const wrap = btn.closest('[data-bl-video-wrap]');
        if (!wrap) return;
        const video = wrap.querySelector('video');
        if (!video) return;
        btn.style.display = 'none';
        video.setAttribute('controls', 'controls');
        video.play().catch(() => {});
      });
    });

    // Sticky CTA hide when drawer open
    const drawerEl = document.querySelector('[data-bl-drawer]');
    if (drawerEl) {
      const obs = new MutationObserver(() => {
        const open = drawerEl.classList.contains('is-open');
        const sc = document.querySelector('[data-bl-sticky-cta]');
        if (sc) sc.style.display = open ? 'none' : '';
      });
      obs.observe(drawerEl, { attributes: true, attributeFilter: ['class'] });
    }
  });

  window.bl.openDrawer = () => Drawer.open();
  window.bl.closeDrawer = () => Drawer.close();
})();
